import { c as _c } from "react/compiler-runtime";
import { GetResponse } from '@commutifi-fe/interfaces';
import { EnterpriseDepositProps } from '@commutifi/models/EnterpriseDeposits';
import { EnterpriseNotificationTemplateType, EnterpriseNotificationTemplatesProps } from '@commutifi/models/EnterpriseNotificationTemplates';
import { EnterpriseKind } from '@commutifi/models/Enterprises';
import { InventoryProps } from '@commutifi/models/Inventory';
import { PlanProps } from '@commutifi/models/Plan';
import { TransactionProps } from '@commutifi/models/Transaction';
import { QueryKey, keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Paths } from 'shared/interfaces';
import { RequestParams } from 'shared/interfaces/Api';
import { Optional, Overwrite, Primitive, Required } from 'utility-types';
import { logger } from '@commutifi-fe/logger';
import { createCommutifiGetNextPageParam } from '@commutifi-fe/utils';
import { ExtractFnReturnType, MutationConfig, queryClient, QueryConfig, QueryConfigPickRes, QueryResult } from '../../reactQuery';
import { DetailedBooking } from '../bookings';
import { Submission } from '../compliance/api';
import { InvoiceWithRelations } from '../invoices';
import { PaymentMethod, PostPaymentMethodBody } from '../paymentMethods';
import { CommuterDatasetResponse, PostSubEnterpriseBody, SubEnterpriseProps, deleteManager, fetchAccounts, fetchAccountsWallets, fetchAdminDDOTSubmission, fetchAdminDDOTSubmissions, fetchAttributes, fetchEnterpriseDashboardUrl, fetchEnterpriseBalances, fetchEnterpriseBookings, fetchEnterpriseTimelineBookings, fetchEnterpriseChildren, fetchEnterpriseCommuterDatasets, fetchInventoryBookingConflicts, fetchPlanAccountBookingConflicts, fetchEnterpriseDeposits, fetchEnterpriseGroupsWallets, fetchEnterpriseInvoices, fetchEnterpriseNotificationTemplate, fetchEnterprisePaymentMethods, fetchEnterprisePlanServiceLatestInventory, fetchEnterprisePlans, fetchEnterprisePretax, fetchEnterpriseShippingRates, fetchEnterpriseWallets, getEnterpriseMerchants, getEnterpriseServiceBookingsCount, getFundingInstructions, patchEnterprise, patchSubmissionAdmin, postEnterprise, postEnterprisePaymentMethods, postEnterprisePlanPermissions, postManagerAccountEnterprise, postManagers, postSubEnterprise, putNotificationTemplate, setEnterprisePaymentMethodToDefault, fetchEnterpriseInventoryFilters, fetchEnterpriseServicesFilters, fetchEnterpriseBookingsFilters, fetchFeatureFlags, fetchEnterpriseServicesInventory, fetchEnterprisePlanServiceInventory, fetchEnterpriseServiceInventoryBookingConflicts, patchEnterprisePlanServiceInventory } from './api';
import endpoints from './endpoints';
import type { AccountBookingConflictsParams, InventoryBookingConflictsParams, InventoryBookingConflictsResponse, PlanOption } from './types';

// Stable empty params object shared by all hooks that accept optional queryParams.
// Using an inline `= {}` default creates a new object reference on every call,
// which lands in the queryKey array and triggers QueryObserver.setOptions → setState
// on every render, causing an infinite re-render loop.
const EMPTY_PARAMS: RequestParams = {};
export const enterpriseQueryKeys = {
  notificationTemplate: (enterpriseId: string | undefined, queryParams: Parameters<typeof fetchEnterpriseNotificationTemplate>[1]) => ['enterprises', enterpriseId, 'notification-templates', ...(queryParams ? [queryParams] : [])] as const,
  userInviteTemplate: (enterpriseId: string | undefined, queryParams?: Parameters<typeof fetchEnterpriseNotificationTemplate>[1]) => [enterpriseQueryKeys.notificationTemplate(enterpriseId, queryParams), 'user-invite-template'] as const,
  managerInviteTemplate: (enterpriseId: string | undefined, queryParams?: Parameters<typeof fetchEnterpriseNotificationTemplate>[1]) => [enterpriseQueryKeys.notificationTemplate(enterpriseId, queryParams), 'manager-invite-template'] as const,
  inventory: (enterpriseId: string) => ['enterprises', enterpriseId, 'inventory'] as const,
  bookings: (enterpriseId: string) => ['enterprises', enterpriseId, 'bookings'] as const,
  services: (enterpriseId: string) => ['enterprises', enterpriseId, 'services'] as const,
  servicesInventory: (enterpriseId: string) => ['enterprises', enterpriseId, 'services', 'inventory'] as const,
  servicesInventoryInfinite: (enterpriseId: string | undefined, queryParams?: object) => ['enterprises', enterpriseId, 'services', 'inventory', 'infinite', ...(queryParams ? [queryParams] : [])] as const,
  paymentMethods: (enterpriseId: string | undefined, queryParams?: Parameters<typeof fetchEnterprisePaymentMethods>[1]) => ['paymentMethods', enterpriseId, ...(queryParams ? [queryParams] : [])] as const,
  planAccountBookingConflicts: (enterpriseId: string | undefined, planId: string | undefined, queryParams: Partial<AccountBookingConflictsParams>) => ['enterprises', enterpriseId, 'plans', planId, 'account-booking-conflicts', queryParams] as const
};
export function useEnterpriseAccounts(kind, enterpriseId, queryParams, options) {
  const $ = _c(9);
  let t0;
  let t1;
  if ($[0] !== enterpriseId || $[1] !== kind || $[2] !== queryParams) {
    t0 = ["enterprise-accounts", enterpriseId, queryParams, kind];
    t1 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id");
      }
      return fetchAccounts(kind, enterpriseId, queryParams);
    };
    $[0] = enterpriseId;
    $[1] = kind;
    $[2] = queryParams;
    $[3] = t0;
    $[4] = t1;
  } else {
    t0 = $[3];
    t1 = $[4];
  }
  let t2;
  if ($[5] !== options || $[6] !== t0 || $[7] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[5] = options;
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return useQuery(t2);
}
export function useEnterpriseChildren(enterpriseId, t0, options) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = ["enterprises", enterpriseId, "sub-enterprises", queryParams];
    t3 = () => fetchEnterpriseChildren(enterpriseId, queryParams);
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
}

/**
 * Same as useEnterpriseChildren but will target a single enterprise and add a limit of 1
 * to make sure we get a single child of the enterprise
 */
export function useEnterpriseChild(organizationId, subEnterpriseId, t0, t1) {
  const $ = _c(12);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const queryParams = t2;
  let t3;
  if ($[2] !== t1) {
    t3 = t1 === undefined ? {} : t1;
    $[2] = t1;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const options = t3;
  let t4;
  if ($[4] !== queryParams || $[5] !== subEnterpriseId) {
    t4 = {
      id: subEnterpriseId,
      limit: 1,
      ...queryParams
    };
    $[4] = queryParams;
    $[5] = subEnterpriseId;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== options.initialData) {
    t5 = options?.initialData ? {
      records: [options?.initialData],
      _metadata: {
        totalCount: 1,
        total: 1,
        offset: 1,
        limit: 1
      }
    } : undefined;
    $[7] = options.initialData;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = !options.initialData;
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = {
      initialData: t5,
      enabled: t6,
      select: _temp
    };
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  return useEnterpriseChildren(organizationId, t4, t7);
}

// After this date we don't have any more legacy invoices
function _temp(t0) {
  const {
    records
  } = t0;
  return records[0];
}
const endOfLegacyInvoices = '2021-08-30T00:00:00.000Z';
export const useFetchEnterpriseInvoices = (enterpriseId, queryParams, options) => {
  const $ = _c(17);
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = ["enterprises", enterpriseId, "invoices"];
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const baseQueryKey = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      "gte:createdAt": endOfLegacyInvoices,
      sort: "-createdAt"
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const defaultQuery = t1;
  let t2;
  if ($[3] !== baseQueryKey || $[4] !== enterpriseId || $[5] !== queryParams) {
    t2 = [...baseQueryKey, enterpriseId, defaultQuery, queryParams];
    $[3] = baseQueryKey;
    $[4] = enterpriseId;
    $[5] = queryParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== enterpriseId || $[8] !== queryParams) {
    t3 = async () => fetchEnterpriseInvoices(enterpriseId, {
      ...defaultQuery,
      ...queryParams
    });
    $[7] = enterpriseId;
    $[8] = queryParams;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== options || $[11] !== t2 || $[12] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options,
      placeholderData: keepPreviousData
    };
    $[10] = options;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const paginationQueryRes = useQuery(t4);
  let t5;
  if ($[14] !== baseQueryKey || $[15] !== paginationQueryRes) {
    t5 = {
      ...paginationQueryRes,
      baseQueryKey,
      defaultQuery
    };
    $[14] = baseQueryKey;
    $[15] = paginationQueryRes;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const res = t5;
  return res;
};
export const useFetchEnterpriseDeposits = (enterpriseId, queryParams) => {
  const $ = _c(3);
  let t0;
  if ($[0] !== enterpriseId || $[1] !== queryParams) {
    t0 = {
      queryKey: ["enterprises", enterpriseId, "topups", queryParams],
      queryFn: async () => {
        if (!enterpriseId) {
          throw new Error("Missing enterprise id");
        }
        const data = await fetchEnterpriseDeposits(enterpriseId, {
          sort: "-createdAt",
          ...queryParams
        });
        return data;
      }
    };
    $[0] = enterpriseId;
    $[1] = queryParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return useQuery(t0);
};
export function useEnterpriseAttributes(enterpriseId, t0, options) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = ["enterprises/attributes", enterpriseId, queryParams];
    t3 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id");
      }
      const {
        records
      } = await fetchAttributes(enterpriseId, queryParams);
      return records;
    };
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
}

/**
 * The enterprise's white-label dashboard base URL (its SSO subdomain origin when set, else the
 * default flat URL). Use it to build tenant-correct public links (e.g. invite links) instead of
 * `window.location.origin`, which only reflects whichever host the manager happens to be on.
 */
export function useEnterpriseDashboardUrl(enterpriseId, options) {
  const $ = _c(9);
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = ["enterprises", enterpriseId, "dashboard-url"];
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const t1 = Boolean(enterpriseId);
  let t2;
  if ($[2] !== enterpriseId) {
    t2 = async () => fetchEnterpriseDashboardUrl(enterpriseId as string);
    $[2] = enterpriseId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      enabled: t1,
      queryFn: t2,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
}

/**
 * The base URL to build tenant-correct public links (e.g. invite links) for an enterprise.
 * `inviteBaseUrl` is the enterprise's dashboard origin once the lookup has SUCCEEDED — its subdomain
 * origin, or the default dashboard origin when it has none, since the endpoint always answers with a
 * url — and `undefined` while it is still loading or on error. Returning `undefined` rather than a
 * separate readiness flag makes the invalid state unrepresentable: a caller physically cannot build
 * a link off an unready base, so it can't silently hand out the fallback (possibly wrong-host) link
 * by forgetting to check a flag.
 *
 * `isError` distinguishes a failed lookup from a still-loading one so callers can show a real error
 * state (rather than a permanently-disabled control the user can't explain), and `retry` refetches.
 * `isError` is true only when the INITIAL lookup produced no usable base — a refetch that fails after
 * a prior success keeps the cached base rather than flipping the UI into the error state on a
 * background blip. Callers gate display on `!inviteBaseUrl`, surface the error on `isError`, and pick
 * their own loading/display fallback.
 */
export function useInviteBaseUrl(enterpriseId) {
  const $ = _c(7);
  const {
    data,
    isLoading,
    isLoadingError,
    refetch
  } = useEnterpriseDashboardUrl(enterpriseId);
  let t0;
  if ($[0] !== refetch) {
    t0 = () => {
      refetch();
    };
    $[0] = refetch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const retry = t0;
  const t1 = data === undefined ? undefined : data ?? window.location.origin;
  let t2;
  if ($[2] !== isLoading || $[3] !== isLoadingError || $[4] !== retry || $[5] !== t1) {
    t2 = {
      inviteBaseUrl: t1,
      isLoading,
      isError: isLoadingError,
      retry
    };
    $[2] = isLoading;
    $[3] = isLoadingError;
    $[4] = retry;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
export function useEnterpriseAccountsWallets(enterpriseId, queryParams, options) {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== enterpriseId || $[1] !== queryParams) {
    t0 = ["enterprises", enterpriseId, "accounts-wallets", queryParams];
    t1 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id");
      }
      return fetchAccountsWallets(enterpriseId, queryParams);
    };
    $[0] = enterpriseId;
    $[1] = queryParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  let t2;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useQuery(t2);
}
export function useEnterprisePlans(enterpriseId, t0, options) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = ["enterprises", enterpriseId, "plans", queryParams];
    t3 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id");
      }
      const {
        records
      } = await fetchEnterprisePlans(enterpriseId, queryParams);
      return records;
    };
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4) as QueryResult<T>;
}
export function useEnterprisePlanServiceLatestInventory(enterpriseId, planId, t0, options) {
  const $ = _c(12);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== planId || $[4] !== queryParams) {
    t2 = ["enterprises", enterpriseId, "plans", planId, "services/inventory", queryParams];
    t3 = async () => {
      if (!enterpriseId || !planId) {
        throw new Error("Missing enterprise id or plan id");
      }
      return fetchEnterprisePlanServiceLatestInventory(enterpriseId, planId, queryParams);
    };
    $[2] = enterpriseId;
    $[3] = planId;
    $[4] = queryParams;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  const t4 = Boolean(enterpriseId && planId);
  let t5;
  if ($[7] !== options || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      ...options
    };
    $[7] = options;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return useQuery(t5) as QueryResult<T>;
}
export const useEnterpriseBookings = (enterpriseId, queryParams, options) => {
  const $ = _c(9);
  let t0;
  let t1;
  if ($[0] !== enterpriseId || $[1] !== queryParams) {
    t0 = [...enterpriseQueryKeys.bookings(enterpriseId), queryParams];
    t1 = async () => {
      if (!enterpriseId) {
        throw Error("Cannot fetch enterprise bookings without an enterpriseId parameter");
      }
      return fetchEnterpriseBookings(enterpriseId, queryParams);
    };
    $[0] = enterpriseId;
    $[1] = queryParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = Boolean(enterpriseId);
  let t3;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
};
export const useEnterpriseTimelineBookings = (enterpriseId, queryParams, options) => {
  const $ = _c(9);
  let t0;
  let t1;
  if ($[0] !== enterpriseId || $[1] !== queryParams) {
    t0 = [...enterpriseQueryKeys.bookings(enterpriseId), "timeline", queryParams];
    t1 = async () => {
      if (!enterpriseId) {
        throw Error("Cannot fetch enterprise timeline bookings without an enterpriseId parameter");
      }
      return fetchEnterpriseTimelineBookings(enterpriseId, queryParams);
    };
    $[0] = enterpriseId;
    $[1] = queryParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  const t2 = Boolean(enterpriseId);
  let t3;
  if ($[4] !== options || $[5] !== t0 || $[6] !== t1 || $[7] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      ...options
    };
    $[4] = options;
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useQuery(t3);
};
export const useEnterpriseServiceBookingsCount = (enterpriseId, serviceId, t0, options) => {
  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams || $[4] !== serviceId) {
    t2 = ["enterprises", enterpriseId, "services", serviceId, "bookings", "count", queryParams];
    t3 = async () => {
      if (!serviceId) {
        throw Error("Missing service id to get service bookings count");
      }
      return getEnterpriseServiceBookingsCount(enterpriseId, serviceId, queryParams);
    };
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = serviceId;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== options || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[7] = options;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return useQuery(t4);
};
export function useEnterpriseWalletsGroups(enterpriseId, queryParams, options) {
  const $ = _c(12);
  let t0;
  if ($[0] !== queryParams) {
    t0 = queryParams ? [queryParams] : [];
    $[0] = queryParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== enterpriseId || $[3] !== t0) {
    t1 = [enterpriseId, "groups-wallets", ...t0];
    $[2] = enterpriseId;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== enterpriseId || $[6] !== queryParams) {
    t2 = async () => {
      if (!enterpriseId) {
        throw Error("Cannot fetch enterprise wallets groups without an enterpriseId");
      }
      return fetchEnterpriseGroupsWallets(enterpriseId, queryParams ?? EMPTY_PARAMS);
    };
    $[5] = enterpriseId;
    $[6] = queryParams;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== options || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      ...options
    };
    $[8] = options;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return useQuery(t3);
}
export function useEnterpriseWallets(enterpriseId, t0, options) {
  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = ["enterprises", enterpriseId, "wallets", queryParams];
    t3 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id to fetch wallets of a specific enterprise");
      }
      return fetchEnterpriseWallets(enterpriseId, queryParams);
    };
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  const t4 = Boolean(enterpriseId);
  let t5;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return useQuery(t5);
}
export function useEnterpriseBalances(enterpriseId, t0, options) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = ["enterprises", enterpriseId, "balances", queryParams];
    t3 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id");
      }
      return fetchEnterpriseBalances(enterpriseId, queryParams);
    };
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
}
export function useAdminDDOTSubmissions(orgId, t0, options) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== orgId || $[3] !== queryParams) {
    t2 = ["organizations", orgId, "ddot-submissions", queryParams];
    t3 = async () => fetchAdminDDOTSubmissions(orgId, queryParams);
    $[2] = orgId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
}
export const useAdminDDOTSubmission = (organizationId, submissionId, t0, options) => {
  const $ = _c(11);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== organizationId || $[3] !== queryParams || $[4] !== submissionId) {
    t2 = ["organizations", organizationId, "ddot-submissions", submissionId, queryParams];
    t3 = async () => fetchAdminDDOTSubmission(organizationId, submissionId, queryParams);
    $[2] = organizationId;
    $[3] = queryParams;
    $[4] = submissionId;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  let t4;
  if ($[7] !== options || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[7] = options;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return useQuery(t4);
};
export const usePatchSubmissionAdmin = (orgId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== orgId) {
    t0 = t1 => {
      const {
        id,
        ...body
      } = t1;
      return patchSubmissionAdmin(orgId, id, body);
    };
    $[0] = orgId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const usePatchEnterprise = (enterpriseId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = body => patchEnterprise(enterpriseId, body);
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const useCreateEnterprise = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp2,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const usePostManagerAccountEnterprise = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp3,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const usePostSubEnterprise = (organizationId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== organizationId) {
    t0 = body => postSubEnterprise(organizationId, body);
    $[0] = organizationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const usePostManagers = (organizationId, queryKey, options) => {
  const $ = _c(10);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== organizationId) {
    t0 = t1 => {
      const {
        body,
        queryParams
      } = t1;
      return postManagers(organizationId, body, queryParams);
    };
    $[0] = organizationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== queryClient || $[4] !== queryKey) {
    t1 = (...t2) => {
      const args = t2;
      if (queryKey) {
        queryClient.refetchQueries({
          queryKey
        });
      }
      if (options?.onSuccess) {
        options.onSuccess(...args);
      }
    };
    $[2] = options;
    $[3] = queryClient;
    $[4] = queryKey;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      mutationFn: t0,
      ...options,
      onSuccess: t1
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
};
export const managersKeys = {
  list: (organizationId: string) => ['list', endpoints.GET.OrganizationManagers.route(organizationId)] as const
};
export const useDeleteManager = (enterpriseId, queryKey, options) => {
  const $ = _c(10);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = managerId => deleteManager(enterpriseId, managerId);
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== queryClient || $[4] !== queryKey) {
    t1 = (...t2) => {
      const args = t2;
      if (queryKey) {
        queryClient.refetchQueries({
          queryKey
        });
      }
      if (options?.onSuccess) {
        options.onSuccess(...args);
      }
    };
    $[2] = options;
    $[3] = queryClient;
    $[4] = queryKey;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      mutationFn: t0,
      ...options,
      onSuccess: t1
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
};
export const useBatchDeleteManagers = (queryKey, organizationId) => {
  const $ = _c(8);
  const queryClient = useQueryClient();
  const {
    mutateAsync: unassignManager
  } = useDeleteManager(organizationId);
  let t0;
  if ($[0] !== unassignManager) {
    t0 = async managerIds => {
      const deletePromises = managerIds.map(managerId => unassignManager(managerId));
      await Promise.all(deletePromises);
    };
    $[0] = unassignManager;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== queryClient || $[3] !== queryKey) {
    t1 = () => {
      queryClient.refetchQueries({
        queryKey
      });
    };
    $[2] = queryClient;
    $[3] = queryKey;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
};
export const useCreateEnterprisePlanPermissions = (enterpriseId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = body => postEnterprisePlanPermissions(enterpriseId, body);
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};

// Enterprise notification templates
// ------------------------------------------------------------------------------------------
export function useEnterpriseNotificationTemplate(enterpriseId, queryParams, options) {
  const $ = _c(16);
  let t0;
  if ($[0] !== enterpriseId || $[1] !== queryParams) {
    t0 = enterpriseQueryKeys.notificationTemplate(enterpriseId, queryParams);
    $[0] = enterpriseId;
    $[1] = queryParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== enterpriseId || $[4] !== queryParams) {
    t1 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id, disabling GET enterprise notifications details details (/enterprises/:enterpriseId/notifications/templates)");
      }
      return fetchEnterpriseNotificationTemplate(enterpriseId, queryParams);
    };
    $[3] = enterpriseId;
    $[4] = queryParams;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const query = useQuery(t2);
  let t3;
  if ($[10] !== enterpriseId || $[11] !== queryParams) {
    t3 = enterpriseQueryKeys.notificationTemplate(enterpriseId, queryParams);
    $[10] = enterpriseId;
    $[11] = queryParams;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== query || $[14] !== t3) {
    t4 = [query, t3];
    $[13] = query;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
type EnterpriseNotificationInviteTemplate = Overwrite<EnterpriseNotificationTemplatesProps, {
  variables: {
    invitationSubject?: string;
    invitationCustomText?: string;
  };
}>;
export function useEnterpriseManagerInviteTemplate(enterpriseId, queryParams, t0) {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let paramsWithDefaults;
  let t2;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    paramsWithDefaults = {
      type: EnterpriseNotificationTemplateType.MANAGER_INVITE,
      limit: 1,
      ...queryParams
    };
    t2 = enterpriseQueryKeys.managerInviteTemplate(enterpriseId, paramsWithDefaults);
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = paramsWithDefaults;
    $[5] = t2;
  } else {
    paramsWithDefaults = $[4];
    t2 = $[5];
  }
  const queryKey = t2;
  let t3;
  if ($[6] !== options || $[7] !== queryKey) {
    t3 = {
      queryKey,
      select: _temp4,
      ...options
    };
    $[6] = options;
    $[7] = queryKey;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useEnterpriseNotificationTemplate(enterpriseId, paramsWithDefaults, t3);
}
function _temp4(t0) {
  const {
    records
  } = t0;
  return records[0];
}
export function useEnterpriseAccountInviteTemplate(enterpriseId, queryParams, t0) {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  let paramsWithDefaults;
  let t2;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    paramsWithDefaults = {
      type: EnterpriseNotificationTemplateType.ACCOUNT_INVITE,
      limit: 1,
      ...queryParams
    };
    t2 = enterpriseQueryKeys.userInviteTemplate(enterpriseId, paramsWithDefaults);
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = paramsWithDefaults;
    $[5] = t2;
  } else {
    paramsWithDefaults = $[4];
    t2 = $[5];
  }
  const queryKey = t2;
  let t3;
  if ($[6] !== options || $[7] !== queryKey) {
    t3 = {
      queryKey,
      select: _temp5,
      ...options
    };
    $[6] = options;
    $[7] = queryKey;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return useEnterpriseNotificationTemplate(enterpriseId, paramsWithDefaults, t3);
}
function _temp5(t0) {
  const {
    records
  } = t0;
  return records[0];
}
export const usePutNotificationTemplate = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp6,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useEnterpriseMerchants = (enterpriseId, t0, options) => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = ["enterprises", enterpriseId, "merchants", queryParams];
    t3 = async () => getEnterpriseMerchants(enterpriseId, queryParams);
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
};
export const useSingleEnterpriseMerchant = (enterpriseId, t0, options) => {
  const $ = _c(8);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = data => {
      const [firstMerchant] = data.records;
      if (!firstMerchant) {
        logger.captureWarning("No merchant found for enterprise", {
          enterpriseId,
          queryParams
        });
      }
      return firstMerchant;
    };
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== options || $[6] !== t2) {
    t3 = {
      ...options,
      select: t2
    };
    $[5] = options;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useEnterpriseMerchants(enterpriseId, queryParams, t3);
};
export const useShippingRates = enterpriseId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = {
      queryKey: ["enterprises", enterpriseId, "shipping-rates"],
      queryFn: async () => {
        if (!enterpriseId) {
          throw Error("Cannot fetch virtual the shipping rates without an enterprise id");
        }
        return fetchEnterpriseShippingRates(enterpriseId);
      }
    };
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useQuery(t0);
};
export const usePostEnterprisePaymentMethod = (enterpriseId, options) => {
  const $ = _c(5);
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = body => {
      if (!enterpriseId) {
        throw new Error("Missing enterprise id");
      }
      return postEnterprisePaymentMethods(enterpriseId, body);
    };
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== options || $[3] !== t0) {
    t1 = {
      mutationFn: t0,
      ...options
    };
    $[2] = options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useMutation(t1);
};
export const useSetEnterprisePaymentMethodToDefault = options => {
  const $ = _c(2);
  let t0;
  if ($[0] !== options) {
    t0 = {
      mutationFn: _temp7,
      ...options
    };
    $[0] = options;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useEnterprisePaymentMethods = (enterpriseId, queryParams, options) => {
  const $ = _c(17);
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = paymentMethod => {
      queryClient.setQueryData(enterpriseQueryKeys.paymentMethods(enterpriseId), old => old ? [...old, paymentMethod] : [paymentMethod]);
    };
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const optimisticUpdate = t0;
  let t1;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t1 = enterpriseQueryKeys.paymentMethods(enterpriseId, queryParams);
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== enterpriseId || $[6] !== queryParams) {
    t2 = async () => {
      if (!enterpriseId) {
        throw new Error("Missing enterprise id");
      }
      const {
        records
      } = await fetchEnterprisePaymentMethods(enterpriseId, {
        sort: "-isDefault",
        ...queryParams
      });
      return records;
    };
    $[5] = enterpriseId;
    $[6] = queryParams;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== options || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      ...options
    };
    $[8] = options;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const queryResult = useQuery(t3);
  let t4;
  if ($[12] !== optimisticUpdate) {
    t4 = {
      optimisticUpdate
    };
    $[12] = optimisticUpdate;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== queryResult || $[15] !== t4) {
    t5 = [queryResult, t4];
    $[14] = queryResult;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5 as const;
};
export const useFundingInstructions = (enterpriseId, t0, options) => {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = ["enterprises", enterpriseId, "funding-instructions", queryParams];
    t3 = async () => getFundingInstructions(enterpriseId, queryParams);
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
};
type UseEnterprisePaymentMethodsParams = Parameters<typeof useEnterprisePaymentMethods>;
export const useEnterpriseDefaultPaymentMethod = enterpriseId => {
  const $ = _c(7);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      isDefault: true
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [t1] = useEnterprisePaymentMethods(enterpriseId, t0);
  let data;
  let rest;
  if ($[1] !== t1) {
    ({
      data,
      ...rest
    } = t1);
    $[1] = t1;
    $[2] = data;
    $[3] = rest;
  } else {
    data = $[2];
    rest = $[3];
  }
  const t2 = data?.[0];
  let t3;
  if ($[4] !== rest || $[5] !== t2) {
    t3 = {
      data: t2,
      ...rest
    };
    $[4] = rest;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
export const useFetchEnterpriseCommuterDatasets = (enterpriseId, queryParams, options) => {
  const $ = _c(17);
  let t0;
  if ($[0] !== enterpriseId) {
    t0 = ["enterprises", enterpriseId, "commuter-datasets"];
    $[0] = enterpriseId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const baseQueryKey = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      sort: "-createdAt",
      fetchCommuterCount: true
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const defaultQuery = t1;
  let t2;
  if ($[3] !== baseQueryKey || $[4] !== enterpriseId || $[5] !== queryParams) {
    t2 = [...baseQueryKey, enterpriseId, defaultQuery, queryParams];
    $[3] = baseQueryKey;
    $[4] = enterpriseId;
    $[5] = queryParams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== enterpriseId || $[8] !== queryParams) {
    t3 = async () => fetchEnterpriseCommuterDatasets(enterpriseId, {
      ...defaultQuery,
      ...queryParams
    });
    $[7] = enterpriseId;
    $[8] = queryParams;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== options || $[11] !== t2 || $[12] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options,
      placeholderData: keepPreviousData
    };
    $[10] = options;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const paginationQueryRes = useQuery(t4);
  let t5;
  if ($[14] !== baseQueryKey || $[15] !== paginationQueryRes) {
    t5 = {
      ...paginationQueryRes,
      baseQueryKey,
      defaultQuery
    };
    $[14] = baseQueryKey;
    $[15] = paginationQueryRes;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const res = t5;
  return res;
};
export function useEnterpriseInventoryFilters(enterpriseId, t0, options) {
  const $ = _c(10);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const queryParams = t1;
  let t2;
  let t3;
  if ($[2] !== enterpriseId || $[3] !== queryParams) {
    t2 = ["enterprises", enterpriseId, "inventory-filters", queryParams];
    t3 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id");
      }
      return fetchEnterpriseInventoryFilters(enterpriseId, queryParams);
    };
    $[2] = enterpriseId;
    $[3] = queryParams;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  let t4;
  if ($[6] !== options || $[7] !== t2 || $[8] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3,
      ...options
    };
    $[6] = options;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return useQuery(t4);
}
export function useEnterpriseServicesFilters(enterpriseId, queryParams, options) {
  const $ = _c(12);
  let t0;
  if ($[0] !== queryParams) {
    t0 = queryParams ? [queryParams] : [];
    $[0] = queryParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== enterpriseId || $[3] !== t0) {
    t1 = ["enterprises", enterpriseId, "services-filters", ...t0];
    $[2] = enterpriseId;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== enterpriseId || $[6] !== queryParams) {
    t2 = async () => {
      if (!enterpriseId) {
        throw Error("Missing enterprise id");
      }
      return fetchEnterpriseServicesFilters(enterpriseId, queryParams ?? EMPTY_PARAMS);
    };
    $[5] = enterpriseId;
    $[6] = queryParams;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== options || $[9] !== t1 || $[10] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: t2,
      ...options
    };
    $[8] = options;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return useQuery(t3);
}
export function useEnterpriseBookingsFilters(organizationId, options) {
  const $ = _c(11);
  let t0;
  if ($[0] !== organizationId) {
    t0 = endpoints.GET.EnterpriseBookingsFilters.route(organizationId || "");
    $[0] = organizationId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = ["filters", t0];
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== organizationId) {
    t2 = () => {
      if (!organizationId) {
        throw new Error("Missing organization id");
      }
      return fetchEnterpriseBookingsFilters(organizationId);
    };
    $[4] = organizationId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = Boolean(organizationId);
  let t4;
  if ($[6] !== options || $[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      ...options
    };
    $[6] = options;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return useQuery(t4);
}
export const useEnterpriseAllowDashboardAccess = (enterpriseId, options) => {
  const $ = _c(7);
  let t0;
  let t1;
  if ($[0] !== enterpriseId) {
    t0 = ["enterprises", enterpriseId, "feature-flags"];
    t1 = () => fetchFeatureFlags(enterpriseId);
    $[0] = enterpriseId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  let t2;
  if ($[3] !== options || $[4] !== t0 || $[5] !== t1) {
    t2 = {
      queryKey: t0,
      queryFn: t1,
      select: _temp8,
      ...options
    };
    $[3] = options;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return useQuery(t2);
};
export const useInventoryBookingConflicts = (enterpriseId, serviceId, inventoryId, queryParams, options) => {
  const $ = _c(11);
  let t0;
  let t1;
  if ($[0] !== enterpriseId || $[1] !== inventoryId || $[2] !== queryParams || $[3] !== serviceId) {
    t0 = ["enterprises", enterpriseId, "services", serviceId, "inventory", inventoryId, "booking-conflicts", queryParams];
    t1 = async () => {
      if (!enterpriseId || !serviceId || !inventoryId) {
        throw new Error("Missing required parameters for conflict detection");
      }
      return fetchInventoryBookingConflicts(enterpriseId, serviceId, inventoryId, queryParams);
    };
    $[0] = enterpriseId;
    $[1] = inventoryId;
    $[2] = queryParams;
    $[3] = serviceId;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  const t2 = !!enterpriseId && !!serviceId && !!inventoryId && options?.enabled !== false;
  let t3;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useQuery(t3);
};

/**
 * The commuter's other bookings on the plan's individual service overlapping the dates, for a manager
 * booking on their behalf (no item is assigned yet, so this doesn't go through an inventory).
 */
export const usePlanAccountBookingConflicts = (enterpriseId, planId, queryParams, options) => {
  const $ = _c(13);
  let t0;
  if ($[0] !== enterpriseId || $[1] !== planId || $[2] !== queryParams) {
    t0 = enterpriseQueryKeys.planAccountBookingConflicts(enterpriseId, planId, queryParams);
    $[0] = enterpriseId;
    $[1] = planId;
    $[2] = queryParams;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== enterpriseId || $[5] !== planId || $[6] !== queryParams) {
    t1 = async () => {
      const {
        accountId,
        intervalStart
      } = queryParams;
      if (!enterpriseId || !planId || !accountId || !intervalStart) {
        throw new Error("Missing required parameters for account conflict detection");
      }
      return fetchPlanAccountBookingConflicts(enterpriseId, planId, {
        ...queryParams,
        accountId,
        intervalStart
      });
    };
    $[4] = enterpriseId;
    $[5] = planId;
    $[6] = queryParams;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const t2 = !!enterpriseId && !!planId && !!queryParams.accountId && !!queryParams.intervalStart && options?.enabled !== false;
  let t3;
  if ($[8] !== options || $[9] !== t0 || $[10] !== t1 || $[11] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      retry: false,
      ...options,
      enabled: t2
    };
    $[8] = options;
    $[9] = t0;
    $[10] = t1;
    $[11] = t2;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  return useQuery(t3);
};
export function useEnterpriseServicesInventory(enterpriseId, queryParams, options) {
  const $ = _c(13);
  let t0;
  if ($[0] !== queryParams) {
    t0 = queryParams ? [queryParams] : [];
    $[0] = queryParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== enterpriseId || $[3] !== t0) {
    t1 = ["enterprises", enterpriseId, "services", "inventory", ...t0];
    $[2] = enterpriseId;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== enterpriseId || $[6] !== queryParams) {
    t2 = async () => {
      if (!enterpriseId) {
        throw new Error("Missing enterprise id");
      }
      return fetchEnterpriseServicesInventory(enterpriseId, queryParams ?? EMPTY_PARAMS);
    };
    $[5] = enterpriseId;
    $[6] = queryParams;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const t3 = Boolean(enterpriseId);
  let t4;
  if ($[8] !== options || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      ...options
    };
    $[8] = options;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return useQuery(t4);
}
export function useEnterpriseServicesInventoryInfinite(enterpriseId, queryParams) {
  const $ = _c(10);
  let t0;
  if ($[0] !== enterpriseId || $[1] !== queryParams) {
    t0 = enterpriseQueryKeys.servicesInventoryInfinite(enterpriseId, queryParams);
    $[0] = enterpriseId;
    $[1] = queryParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== enterpriseId || $[4] !== queryParams) {
    t1 = async t2 => {
      const {
        pageParam
      } = t2;
      if (!enterpriseId) {
        throw new Error("Missing enterprise id");
      }
      return fetchEnterpriseServicesInventory(enterpriseId, {
        ...queryParams,
        offset: pageParam
      });
    };
    $[3] = enterpriseId;
    $[4] = queryParams;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const t2 = Boolean(enterpriseId);
  let t3;
  if ($[6] !== t0 || $[7] !== t1 || $[8] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      getNextPageParam: createCommutifiGetNextPageParam,
      initialPageParam: 0,
      enabled: t2
    };
    $[6] = t0;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return useInfiniteQuery(t3);
}
export function useEnterprisePlanServiceInventory(enterpriseId, planId, queryParams, options) {
  const $ = _c(15);
  let t0;
  if ($[0] !== queryParams) {
    t0 = queryParams ? [queryParams] : [];
    $[0] = queryParams;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== enterpriseId || $[3] !== planId || $[4] !== t0) {
    t1 = ["enterprises", enterpriseId, "plans", planId, "services", "inventory", ...t0];
    $[2] = enterpriseId;
    $[3] = planId;
    $[4] = t0;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== enterpriseId || $[7] !== planId || $[8] !== queryParams) {
    t2 = async () => {
      if (!enterpriseId || !planId) {
        throw new Error("Missing enterprise id or plan id");
      }
      return fetchEnterprisePlanServiceInventory(enterpriseId, planId, queryParams ?? EMPTY_PARAMS);
    };
    $[6] = enterpriseId;
    $[7] = planId;
    $[8] = queryParams;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const t3 = Boolean(enterpriseId && planId);
  let t4;
  if ($[10] !== options || $[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      ...options
    };
    $[10] = options;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return useQuery(t4);
}
export function useEnterpriseServiceInventoryBookingConflicts(enterpriseId, serviceId, inventoryId, params, options) {
  const $ = _c(11);
  let t0;
  let t1;
  if ($[0] !== enterpriseId || $[1] !== inventoryId || $[2] !== params || $[3] !== serviceId) {
    t0 = ["enterprises", enterpriseId, "services", serviceId, "inventory", inventoryId, "booking-conflicts", params];
    t1 = () => fetchEnterpriseServiceInventoryBookingConflicts(enterpriseId, serviceId, inventoryId, params);
    $[0] = enterpriseId;
    $[1] = inventoryId;
    $[2] = params;
    $[3] = serviceId;
    $[4] = t0;
    $[5] = t1;
  } else {
    t0 = $[4];
    t1 = $[5];
  }
  const t2 = !!enterpriseId && !!serviceId && !!inventoryId && !!params.intervalStart;
  let t3;
  if ($[6] !== options || $[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      ...options
    };
    $[6] = options;
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return useQuery(t3);
}
export function usePatchEnterprisePlanServiceInventory() {
  const $ = _c(2);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== queryClient) {
    t0 = {
      mutationFn: _temp9,
      onSuccess: (_, variables) => {
        queryClient.invalidateQueries({
          queryKey: enterpriseQueryKeys.inventory(variables.enterpriseId)
        });
        queryClient.invalidateQueries({
          queryKey: enterpriseQueryKeys.services(variables.enterpriseId)
        });
        queryClient.invalidateQueries({
          queryKey: enterpriseQueryKeys.servicesInventory(variables.enterpriseId)
        });
      }
    };
    $[0] = queryClient;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
}
function _temp9(t0) {
  const {
    enterpriseId,
    serviceId,
    inventoryId,
    body
  } = t0;
  return patchEnterprisePlanServiceInventory(enterpriseId, serviceId, inventoryId, body);
}
export function useEnterprisePretax(enterpriseId, options) {
  const $ = _c(8);
  let t0;
  let t1;
  if ($[0] !== enterpriseId) {
    t0 = ["enterprises", enterpriseId, "pretax"];
    t1 = async () => {
      if (!enterpriseId) {
        throw new Error("Missing enterprise id");
      }
      return fetchEnterprisePretax(enterpriseId);
    };
    $[0] = enterpriseId;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  const t2 = Boolean(enterpriseId);
  let t3;
  if ($[3] !== options || $[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      ...options
    };
    $[3] = options;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return useQuery(t3);
}
function _temp2(body) {
  return postEnterprise(body);
}
function _temp3(body) {
  return postManagerAccountEnterprise(body);
}
function _temp6(t0) {
  const {
    enterpriseId,
    body
  } = t0;
  return putNotificationTemplate(enterpriseId, body);
}
function _temp7(t0) {
  const {
    enterpriseId,
    paymentMethodId
  } = t0;
  if (!enterpriseId || !paymentMethodId) {
    throw new Error("Missing enterprise id or paymentMethod id");
  }
  return setEnterprisePaymentMethodToDefault(enterpriseId, paymentMethodId);
}
function _temp8(data) {
  return data.records[0]?.userDashboard;
}